if (!customElements.get('cutoff-timer')) {
  customElements.define(
    'cutoff-timer',
    class CutoffTimer extends HTMLElement {
      constructor() {
        super();

        this.init();
      }

      init() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.dateOutput = this.querySelector('[data-delivery-date]');
        this.timer = this.querySelector('[data-cutoff-timer]');
        this.countdown = this.querySelector('[data-cutoff-countdown]');

        this.showSeconds = this.dataset.showSeconds === 'true';
        this.utcOffset = this.parseOffset(this.dataset.utcOffset);
        this.cutoff = this.parseTimeOfDay(this.dataset.endTime);
        this.duration = this.parseDuration(this.dataset.duration);

        // Without a cutoff there is nothing to count down to, and the
        // server-rendered delivery date already stands on its own.
        if (this.cutoff === null) {
          return;
        }

        this.updateDisplay();
        this.intervalId = setInterval(() => this.updateDisplay(), this.MS_IN_SEC);
      }

      disconnectedCallback() {
        clearInterval(this.intervalId);
      }

      // Milliseconds elapsed since midnight in the store timezone, so every
      // visitor sees the same countdown regardless of their own timezone.
      msSinceStoreMidnight() {
        const shifted = Date.now() + this.utcOffset;
        return ((shifted % this.MS_IN_DAY) + this.MS_IN_DAY) % this.MS_IN_DAY;
      }

      updateDisplay() {
        const elapsed = this.msSinceStoreMidnight();
        const pastCutoff = elapsed >= this.cutoff;

        this.updateDeliveryDate(pastCutoff);
        this.updateCountdown(elapsed);
      }

      // Orders placed after the cutoff ship a day later.
      updateDeliveryDate(pastCutoff) {
        if (!this.dateOutput) {
          return;
        }

        const date = pastCutoff ? this.dateOutput.dataset.afterCutoff : this.dateOutput.dataset.beforeCutoff;
        if (date && this.dateOutput.textContent !== date) {
          this.dateOutput.textContent = date;
        }
      }

      updateCountdown(elapsed) {
        if (!this.timer || !this.countdown) {
          return;
        }

        // The countdown runs out at the cutoff and resets to its full duration
        // there, then again every `duration` hours until the next cutoff.
        const sinceCutoff = (((elapsed - this.cutoff) % this.MS_IN_DAY) + this.MS_IN_DAY) % this.MS_IN_DAY;
        const remaining = this.duration - (sinceCutoff % this.duration);

        this.countdown.textContent = this.formatRemainingTime(remaining);
        this.timer.classList.remove('hidden');
      }

      formatRemainingTime(ms) {
        const parts = [
          `${this.addLeadingZero(Math.floor(ms / this.MS_IN_HRS))}h`,
          `${this.addLeadingZero(Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN))}m`,
        ];

        if (this.showSeconds) {
          parts.push(`${this.addLeadingZero(Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC))}s`);
        }

        return parts.join(' ');
      }

      addLeadingZero(value) {
        return value < 10 ? `0${value}` : value.toString();
      }

      // Accepts an offset from UTC such as `+05:30`, `-08:00` or `0530`.
      parseOffset(value) {
        const match = /^([+-])?(\d{1,2}):?(\d{2})$/.exec((value || '').trim());
        if (!match) {
          return 0;
        }

        const sign = match[1] === '-' ? -1 : 1;
        return sign * (Number(match[2]) * this.MS_IN_HRS + Number(match[3]) * this.MS_IN_MIN);
      }

      // How long one countdown cycle runs, in hours.
      parseDuration(value) {
        const hours = Number(value);
        if (!Number.isFinite(hours) || hours <= 0 || hours > 24) {
          return 12 * this.MS_IN_HRS;
        }

        return hours * this.MS_IN_HRS;
      }

      // Accepts a 24-hour time such as `00:00` or `12:00`.
      parseTimeOfDay(value) {
        const match = /^(\d{1,2}):(\d{2})$/.exec((value || '').trim());
        if (!match) {
          return null;
        }

        const hours = Number(match[1]);
        const minutes = Number(match[2]);
        if (hours > 23 || minutes > 59) {
          return null;
        }

        return hours * this.MS_IN_HRS + minutes * this.MS_IN_MIN;
      }
    }
  );
}
